import React, { useEffect, useState } from 'react'
import { formatDate } from '../utils/dateFormat'
import { apiGet } from '../utils/api'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// Функция для форматирования чисел с разделителями тысяч в русской локали
const formatNumber = (num: number): string => {
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface CourtRentalItem {
  id: string
  documentDate: string
  documentNumber: string
  supplierId: string
  supplierName: string
  tournamentId: string
  tournamentName: string
  courtId: string
  courtName: string
  startTime: string
  endTime: string
  hours: number
  ratePerHour: number
  amount: number
  comment: string | null
  documentComment: string | null
}

interface ConsolidatedCourtRentalItem {
  id: string
  documentDate: string
  supplierName: string
  courtName: string
  startTime: string
  endTime: string
  hours: number
  ratePerHour: number
  amount: number
}

type ReportTab = 'matches' | 'courts'

const ReportsCourtRentals: React.FC = () => {
  const [items, setItems] = useState<CourtRentalItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [lastValidFrom, setLastValidFrom] = useState('')
  const [lastValidTo, setLastValidTo] = useState('')
  const [totals, setTotals] = useState({ totalHours: 0, totalAmount: 0 })
  const [tournaments, setTournaments] = useState<any[]>([])
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('')
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('')
  const [activeTab, setActiveTab] = useState<ReportTab>('matches')

  // Загружаем сохраненный период при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('court-rentals-report-from')
    const savedTo = localStorage.getItem('court-rentals-report-to')
    if (savedFrom) setFrom(savedFrom)
    if (savedTo) setTo(savedTo)
    setIsInitialized(true)
  }, [])

  // Загружаем справочники
  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [tournamentsData, suppliersData] = await Promise.all([
          apiGet<any>('/tournaments', { limit: 1000 }),
          apiGet<any>('/suppliers', { limit: 1000 })
        ])
        setTournaments(Array.isArray(tournamentsData.tournaments) ? tournamentsData.tournaments : [])
        setSuppliers(Array.isArray(suppliersData.suppliers) ? suppliersData.suppliers : (Array.isArray(suppliersData.items) ? suppliersData.items : []))
      } catch (e) {
        console.error('Ошибка загрузки справочников:', e)
      }
    }
    loadRefs()
  }, [])

  // Загружаем данные только при реальном изменении дат
  useEffect(() => {
    if (isInitialized) {
      const fromChanged = from !== lastValidFrom && from !== ''
      const toChanged = to !== lastValidTo && to !== ''
      const bothEmpty = from === '' && to === '' && (lastValidFrom !== '' || lastValidTo !== '')
      const initialLoad = from === '' && to === '' && lastValidFrom === '' && lastValidTo === ''

      if (fromChanged || toChanged || bothEmpty || initialLoad) {
        const timer = setTimeout(() => {
          fetchData()
          setLastValidFrom(from)
          setLastValidTo(to)
        }, 300)

        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, from, to, lastValidFrom, lastValidTo, selectedTournamentId, selectedSupplierId])

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('court-rentals-report-from', fromDate)
    } else {
      localStorage.removeItem('court-rentals-report-from')
    }
    if (toDate) {
      localStorage.setItem('court-rentals-report-to', toDate)
    } else {
      localStorage.removeItem('court-rentals-report-to')
    }
  }

  // Сброс периода
  const resetPeriod = () => {
    setFrom('')
    setTo('')
    setSelectedTournamentId('')
    setSelectedSupplierId('')
    setLastValidFrom('')
    setLastValidTo('')
    localStorage.removeItem('court-rentals-report-from')
    localStorage.removeItem('court-rentals-report-to')
  }

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      if (selectedTournamentId) params.tournamentId = selectedTournamentId
      if (selectedSupplierId) params.supplierId = selectedSupplierId

      const data = await apiGet<any>('/reports/court-rentals', params)
      setItems(Array.isArray(data.items) ? data.items : [])
      setTotals(data.totals || { totalHours: 0, totalAmount: 0 })
    } catch (e: any) {
      setError(e.message || 'Ошибка загрузки отчета')
    } finally {
      setLoading(false)
    }
  }

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFrom(e.target.value)
    savePeriod(e.target.value, to)
  }

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTo(e.target.value)
    savePeriod(from, e.target.value)
  }

  const handleUpdate = () => {
    fetchData()
  }

  const exportToPDF = () => {
    if (items.length === 0) {
      alert('Нет данных для экспорта')
      return
    }

    const doc = new jsPDF('l', 'mm', 'a4') // landscape orientation
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 10

    // Фильтры
    let filtersText = ''
    if (selectedTournamentId) {
      const tournament = tournaments.find(t => t.id === selectedTournamentId)
      if (tournament) filtersText += `Турнир: ${tournament.name}`
    }
    if (selectedSupplierId) {
      const supplier = suppliers.find(s => s.id === selectedSupplierId)
      if (supplier) {
        if (filtersText) filtersText += ' | '
        filtersText += `Поставщик: ${supplier.name}`
      }
    }

    // Таблица
    const tableData = items.map(item => [
      formatDate(item.documentDate),
      item.documentNumber || '—',
      item.supplierName,
      item.tournamentName,
      item.courtName,
      item.startTime,
      item.endTime,
      `${item.hours.toFixed(1)} ч`,
      formatNumber(item.ratePerHour),
      formatNumber(item.amount),
      (item.comment || item.documentComment || '—').substring(0, 30) // Ограничиваем длину комментария
    ])

    // Итоговая строка
    tableData.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `${totals.totalHours.toFixed(1)} ч`,
      '',
      formatNumber(totals.totalAmount),
      'Итого'
    ])

    // Заголовки таблицы
    const headers = [
      'Дата',
      'Номер',
      'Поставщик',
      'Турнир',
      'Корт',
      'Начало',
      'Окончание',
      'Часов',
      'Тариф/час',
      'Сумма',
      'Комментарий'
    ]

    // Добавляем заголовок и информацию о периоде в начало таблицы через body
    const titleRow = [['Детальный отчет по аренде кортов', '', '', '', '', '', '', '', '', '', '']]
    const periodRowText = from && to 
      ? `Период: ${formatDate(from)} - ${formatDate(to)}`
      : from
      ? `С: ${formatDate(from)}`
      : to
      ? `По: ${formatDate(to)}`
      : 'Все периоды'
    const periodRow = [[periodRowText, '', '', '', '', '', '', '', '', '', '']]
    const filterRow = filtersText ? [[filtersText, '', '', '', '', '', '', '', '', '', '']] : []
    const emptyRow = [['', '', '', '', '', '', '', '', '', '', '']]

    autoTable(doc, {
      head: [],
      body: [
        ...titleRow,
        ...periodRow,
        ...filterRow,
        ...emptyRow,
        headers,
        ...tableData
      ],
      startY: 15,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        font: 'helvetica',
        fontStyle: 'normal',
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8
      },
      // Стили для строк заголовка и периода
      didParseCell: (data: any) => {
        // Заголовок отчета
        if (data.row.index === 0) {
          data.cell.colSpan = 11
          data.cell.styles.fontSize = 16
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.halign = 'center'
          data.cell.styles.fillColor = false
          data.cell.styles.textColor = [0, 0, 0]
        }
        // Период
        else if (data.row.index === 1) {
          data.cell.colSpan = 11
          data.cell.styles.fontSize = 10
          data.cell.styles.halign = 'center'
          data.cell.styles.fillColor = false
          data.cell.styles.textColor = [0, 0, 0]
        }
        // Фильтры
        else if (data.row.index === 2 && filtersText) {
          data.cell.colSpan = 11
          data.cell.styles.fontSize = 9
          data.cell.styles.halign = 'center'
          data.cell.styles.fillColor = false
          data.cell.styles.textColor = [0, 0, 0]
        }
        // Пустая строка
        else if (data.row.index === (filtersText ? 3 : 2)) {
          data.cell.styles.fillColor = false
          data.cell.styles.lineWidth = 0
          data.cell.styles.minCellHeight = 5
        }
        // Заголовки таблицы (строка с заголовками колонок)
        else if (data.row.index === (filtersText ? 4 : 3)) {
          data.cell.styles.fillColor = [66, 139, 202]
          data.cell.styles.textColor = 255
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fontSize = 8
          data.cell.styles.halign = data.column.index >= 7 && data.column.index <= 9 ? 'right' : 'left'
        }
        // Итоговая строка
        else if (data.row.index === tableData.length + (filtersText ? 4 : 3)) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [230, 230, 230]
        }
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Дата
        1: { cellWidth: 25 }, // Номер
        2: { cellWidth: 35 }, // Поставщик
        3: { cellWidth: 40 }, // Турнир
        4: { cellWidth: 20 }, // Корт
        5: { cellWidth: 20 }, // Начало
        6: { cellWidth: 20 }, // Окончание
        7: { cellWidth: 20, halign: 'right' }, // Часов
        8: { cellWidth: 25, halign: 'right' }, // Тариф/час
        9: { cellWidth: 25, halign: 'right' }, // Сумма
        10: { cellWidth: 40 } // Комментарий
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      margin: { left: margin, right: margin },
      tableWidth: pageWidth - (margin * 2)
    })

    // Имя файла
    const fileName = `Court_Rentals_${from || 'all'}_${to || 'periods'}_${new Date().toISOString().split('T')[0]}.pdf`

    // Сохраняем PDF
    doc.save(fileName)
  }

  const parseTimeToMinutes = (time: string): number => {
    const [h, m] = String(time || '').split(':').map(v => Number(v))
    if (!Number.isFinite(h) || !Number.isFinite(m)) return -1
    return h * 60 + m
  }

  const consolidatedItems: ConsolidatedCourtRentalItem[] = React.useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return []

    const sorted = [...items].sort((a, b) => {
      const dateCmp = String(a.documentDate || '').localeCompare(String(b.documentDate || ''))
      if (dateCmp !== 0) return dateCmp

      const supplierCmp = String(a.supplierName || '').localeCompare(String(b.supplierName || ''))
      if (supplierCmp !== 0) return supplierCmp

      const courtCmp = String(a.courtName || '').localeCompare(String(b.courtName || ''))
      if (courtCmp !== 0) return courtCmp

      const startCmp = parseTimeToMinutes(a.startTime) - parseTimeToMinutes(b.startTime)
      if (startCmp !== 0) return startCmp

      return String(a.id).localeCompare(String(b.id))
    })

    const result: ConsolidatedCourtRentalItem[] = []
    let current: ConsolidatedCourtRentalItem | null = null

    sorted.forEach((item) => {
      const nextItem: ConsolidatedCourtRentalItem = {
        id: item.id,
        documentDate: item.documentDate,
        supplierName: item.supplierName,
        courtName: item.courtName,
        startTime: item.startTime,
        endTime: item.endTime,
        hours: Number(item.hours) || 0,
        ratePerHour: Number(item.ratePerHour) || 0,
        amount: Number(item.amount) || 0
      }

      if (!current) {
        current = nextItem
        return
      }

      const canMerge =
        current.documentDate === nextItem.documentDate &&
        current.supplierName === nextItem.supplierName &&
        current.courtName === nextItem.courtName &&
        Number(current.ratePerHour) === Number(nextItem.ratePerHour) &&
        parseTimeToMinutes(current.endTime) === parseTimeToMinutes(nextItem.startTime)

      if (canMerge) {
        current.endTime = nextItem.endTime
        current.hours += nextItem.hours
        current.amount += nextItem.amount
      } else {
        result.push(current)
        current = nextItem
      }
    })

    if (current) result.push(current)
    return result
  }, [items])

  const consolidatedTotals = React.useMemo(() => {
    return consolidatedItems.reduce(
      (acc, item) => {
        acc.totalHours += item.hours
        acc.totalAmount += item.amount
        return acc
      },
      { totalHours: 0, totalAmount: 0 }
    )
  }, [consolidatedItems])

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Детальный отчет по аренде кортов</h1>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата с
            </label>
            <input
              type="date"
              value={from}
              onChange={handleFromChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата по
            </label>
            <input
              type="date"
              value={to}
              onChange={handleToChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Турнир
            </label>
            <select
              value={selectedTournamentId}
              onChange={(e) => {
                setSelectedTournamentId(e.target.value)
                setTimeout(fetchData, 100)
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Все турниры</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поставщик
            </label>
            <select
              value={selectedSupplierId}
              onChange={(e) => {
                setSelectedSupplierId(e.target.value)
                setTimeout(fetchData, 100)
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Все поставщики</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleUpdate}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Обновить
          </button>
          <button
            onClick={resetPeriod}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Сбросить
          </button>
          <button
            onClick={exportToPDF}
            disabled={items.length === 0 || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📄 Экспорт в PDF
          </button>
        </div>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Таблица */}
      {loading ? (
        <div className="text-center py-8">Загрузка...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="border-b border-gray-200 px-4 pt-3">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('matches')}
                className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 ${
                  activeTab === 'matches'
                    ? 'border-blue-600 text-blue-700 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                По матчам
              </button>
              <button
                onClick={() => setActiveTab('courts')}
                className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 ${
                  activeTab === 'courts'
                    ? 'border-blue-600 text-blue-700 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                По кортам
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {activeTab === 'matches' ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24" style={{ fontSize: '10px' }}>
                      Дата документа
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28" style={{ fontSize: '10px' }}>
                      Номер
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32" style={{ fontSize: '10px' }}>
                      Поставщик
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Турнир
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16" style={{ fontSize: '10px' }}>
                      Корт
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" style={{ fontSize: '10px' }}>
                      Начало
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" style={{ fontSize: '10px' }}>
                      Окончание
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20" style={{ fontSize: '10px' }}>
                      Часов
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24" style={{ fontSize: '10px' }}>
                      Тариф/час
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28" style={{ fontSize: '10px' }}>
                      Сумма
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Комментарий
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-2 py-4 text-center text-sm text-gray-500">
                        Нет данных для отображения
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {formatDate(item.documentDate)}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.documentNumber || '—'}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.supplierName}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.tournamentName}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.courtName}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.startTime}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.endTime}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {item.hours.toFixed(1)} ч
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {formatNumber(item.ratePerHour)} MDL
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {formatNumber(item.amount)} MDL
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-500" style={{ fontSize: '12px' }}>
                          {item.comment || item.documentComment || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={7} className="px-2 py-3 text-right text-sm font-medium text-gray-700" style={{ fontSize: '12px' }}>
                        Итого:
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-bold text-gray-900" style={{ fontSize: '12px' }}>
                        {totals.totalHours.toFixed(1)} ч
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-medium text-gray-700" style={{ fontSize: '12px' }}>
                        —
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-bold text-gray-900" style={{ fontSize: '12px' }}>
                        {formatNumber(totals.totalAmount)} MDL
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Дата
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Поставщик
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Корт
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Начало
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Окончание
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Часов
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Тариф
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                      Сумма
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {consolidatedItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-2 py-4 text-center text-sm text-gray-500">
                        Нет данных для отображения
                      </td>
                    </tr>
                  ) : (
                    consolidatedItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {formatDate(item.documentDate)}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.supplierName}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.courtName}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.startTime}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontSize: '12px' }}>
                          {item.endTime}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {item.hours.toFixed(1)} ч
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {formatNumber(item.ratePerHour)} MDL
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 text-right" style={{ fontSize: '12px' }}>
                          {formatNumber(item.amount)} MDL
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {consolidatedItems.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={5} className="px-2 py-3 text-right text-sm font-medium text-gray-700" style={{ fontSize: '12px' }}>
                        Итого:
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-bold text-gray-900" style={{ fontSize: '12px' }}>
                        {consolidatedTotals.totalHours.toFixed(1)} ч
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-medium text-gray-700" style={{ fontSize: '12px' }}>
                        —
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-bold text-gray-900" style={{ fontSize: '12px' }}>
                        {formatNumber(consolidatedTotals.totalAmount)} MDL
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsCourtRentals

